import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { Card } from '@/components/ui/Card'
import { StatusPill } from '@/components/ui/StatusPill'
import { Tag } from '@/components/ui/Tag'
import { useFormat } from '@/i18n/useFormat'
import { cn } from '@/lib/cn'
import { placeName } from '@/lib/countries'
import { formatMoney } from '@/lib/format'
import { MODE_ICONS, modeLabelKey } from '@/lib/icons'
import type { CaseLeg } from '@/types/domain'

import { useSession } from '@/features/auth/api/useSession'
import { Fact } from '@/features/cases/components/shared/Fact'

import { legCarriersOf } from './legCarriers'
import { LegCarriersLine } from './LegCarriersLine'
import { LegPlace } from './LegPlace'

/* One card per leg (UX mockup § legs): mode disc and status pill on top, the
 * leg's facts — its document, ETD/ATD, ETA/ATA, its cost — in a grid beneath,
 * and the FR-52 stall banner on the leg it is happening to. Cards rather than
 * a threaded list, because each leg now carries a card's worth of facts. */

const LEG_STATUS = {
  planned: 'neutral',
  'in-transit': 'brand',
  'waiting-border': 'attention',
  arrived: 'positive',
  completed: 'positive',
} as const

/** The icon disc restates the pill's tone — colour never carries the status
 *  alone, the pill beside it says the word. */
const LEG_DISC: Record<CaseLeg['status'], string> = {
  planned: 'bg-neutral-100 text-text-muted',
  'in-transit': 'bg-brand-soft text-brand',
  'waiting-border': 'bg-attention-soft text-attention',
  arrived: 'bg-positive-soft text-positive',
  completed: 'bg-positive-soft text-positive',
}

export function LegCard({ caseId, leg }: { caseId: string; leg: CaseLeg }) {
  const { t, i18n } = useTranslation()
  const format = useFormat()
  const { data: session } = useSession()
  const ModeIcon = MODE_ICONS[leg.mode]
  /* Who carries it, by the one rule the leg page's summary uses (S-10): a
     truck with no carrier of its own counts as the leg's, several are counted,
     and a reader not shown agent identity reads «Перевозчик скрыт». */
  const carriers = legCarriersOf(leg, session?.seesAgentIdentity)
  /* The unit counts the card prints — trucks in the fact grid, boxes on their
     strip (S-18b: both are leg units, told apart by kind). */
  const trucks = (leg.units ?? []).filter((unit) => unit.kind === 'truck').length
  const boxes = (leg.units ?? []).filter((unit) => unit.kind === 'container').length

  const notStarted = leg.status === 'planned' || leg.status === 'waiting-border'
  const delayed = (leg.delayDays ?? 0) > 0
  const expense = leg.actualExpense ?? leg.plannedExpense

  /* The whole card is a LINK to the leg's own page (issue #35 story 3), not a
     div wired to open a sheet.

     It used to be the latter, and the difference is not cosmetic: the div had
     to reimplement — and did reimplement, right here — the tabIndex, the
     Enter/Space handling and the accessible name that an anchor carries for
     free, while still giving up middle-click, Cmd-click, "copy link address"
     and the browser's own visited state. A direction is now a URL somebody can
     be sent, so the affordance that opens it is the one the platform already
     has. */
  /* The carriers' disclosure is a BUTTON, and a button inside an anchor is
     invalid markup that a screen reader announces as one control — so since
     S-10 the card is a frame holding the link, and the carriers strip sits in
     the frame beside it rather than inside it. */
  return (
    <Card className="hover:bg-surface-hover transition-colors">
      <Link
        to={`/cases/${caseId}/legs/${leg.id}`}
        aria-label={t('cases.legs.open', {
          n: leg.sequence,
          from: placeName(leg.origin, i18n.language) ?? leg.origin,
          to: placeName(leg.destination, i18n.language) ?? leg.destination,
        })}
        className="block"
      >
        <div className="border-line-hair flex items-center gap-3 border-b px-[18px] py-3">
          <span
            aria-hidden
            className={cn(
              'flex size-8 shrink-0 items-center justify-center rounded-full',
              LEG_DISC[leg.status],
            )}
          >
            <ModeIcon size={15} />
          </span>

          <div className="flex min-w-0 flex-1 flex-col gap-1">
            {/* The position, the KIND of movement, and the movement itself — the
                mode as a tag rather than as a third clause in one sentence,
                because that is what a tag is for (Tag: pills mean lifecycle
                state, tags mean kind), and it is what lets the route read as a
                route instead of as the tail of a run-on line. */}
            <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-[13.5px] leading-tight">
              <span className="text-text-heading font-medium">
                {t('cases.legs.legN', { n: leg.sequence })}
              </span>
              <Tag>{t(modeLabelKey(leg.mode))}</Tag>
              {/* The flag BESIDE the place, never instead of it: the place is the
                  fact somebody reads out on the phone. A leg with no country
                  renders exactly as it did before the field existed — no flag, no
                  gap, no globe standing in for one. The `title` is the country's
                  name, because here nothing else on the line says it. */}
              <span className="text-text-heading min-w-0 truncate font-medium">
                <LegPlace name={leg.origin} country={leg.originCountry} />
                <span className="text-text-faint mx-1.5" aria-hidden>
                  →
                </span>
                <LegPlace name={leg.destination} country={leg.destinationCountry} />
              </span>
            </span>
            {/* No carrier-and-plates line under the title any more, on purpose:
                the header is the route, and who runs it on what is the leg
                page's unit cards — a run of plates here duplicated that table
                one card up and grew with every truck. The COUNTS live in the
                fact grid below (trucks) and the containers strip. */}
          </div>

          <StatusPill tone={LEG_STATUS[leg.status]}>
            {t(`legStatus.${leg.status}`)}
          </StatusPill>
        </div>

        {/* The leg's facts as a STRIP: the dates first, then how long it takes,
            then its paper — left to right in the order somebody checks them, with
            a hairline between neighbours so a wide row reads as columns rather
            than as drifting pairs. The rules are positional (`nth-child`) rather
            than per-fact, because half these cells are conditional and the one
            that starts a row is whichever one lands there. */}
        <div
          className={cn(
            'grid grid-cols-2 gap-y-3 px-[18px] py-3.5 lg:grid-cols-4',
            '[&>*]:border-line-hair [&>*]:border-l [&>*]:pr-4 [&>*]:pl-4',
            '[&>*:nth-child(2n+1)]:border-l-0 [&>*:nth-child(2n+1)]:pl-0',
            'lg:[&>*:nth-child(2n+1)]:border-l lg:[&>*:nth-child(2n+1)]:pl-4',
            'lg:[&>*:nth-child(4n+1)]:border-l-0 lg:[&>*:nth-child(4n+1)]:pl-0',
          )}
        >
          {/* ETD alone while departure is still a plan, ETD / ATD once it moved —
              the label names which facts the dates are, like the overview's
              details card. The abbreviations stay untranslated: they are the
              trade's own words in both offices. */}
          <Fact label={leg.atd ? 'ETD / ATD' : 'ETD'}>
            <span className="asa-num">
              {leg.etd
                ? notStarted
                  ? t('cases.legs.datePlanned', { date: format.dateShort(leg.etd) })
                  : format.dateShort(leg.etd)
                : '—'}
              {leg.atd ? ` · ${format.dateShort(leg.atd)}` : null}
            </span>
          </Fact>

          <Fact label={leg.ata ? 'ETA / ATA' : 'ETA'}>
            <span className="asa-num">
              {leg.eta ? format.dateShort(leg.eta) : '—'}
              {leg.ata ? (
                <>
                  {' · '}
                  {/* A late actual carries its slip with it, in the tone the
                      route card uses for dwell. The delta is SERVED (AD-9). */}
                  <span className={delayed ? 'text-attention' : undefined}>
                    {format.dateShort(leg.ata)}
                    {delayed ? ` ${t('cases.legs.delay', { count: leg.delayDays ?? 0 })}` : null}
                  </span>
                </>
              ) : null}
            </span>
          </Fact>

          {/* How long the leg takes, SERVED (casefile.Leg.TransitDays) rather
              than subtracted from the two dates beside it: a span computed here
              would be a second rule about which end counts, and the reader would
              meet both answers on one card. Zero is a real span and says so in
              words, because "0 days" reads as a leg nobody has dated. */}
          {leg.transitDays != null ? (
            <Fact label={t('cases.legs.duration')}>
              <span className="asa-num">
                {leg.transitDays === 0
                  ? t('cases.legs.sameDay')
                  : t('cases.legs.days', { count: leg.transitDays })}
              </span>
            </Fact>
          ) : null}

          {/* HOW MANY trucks run this leg — the carriage document (CMR) fact
              that used to sit here moved to where the paper itself lives, the
              leg page's «Фуры» card, which carries a CMR per truck (0055): one
              number on a card summarising a per-truck fact was wrong as often
              as the leg had two trucks. Count only, plates on the leg page —
              the same rule the containers strip below follows. */}
          {trucks > 0 ? (
            <Fact label={t('cases.legs.trucks')}>
              <span className="asa-num">{trucks}</span>
            </Fact>
          ) : null}

          {/* The conveyance (FR-26), beside the carriage document rather than
              instead of it — an air leg has an AWB AND a flight number, and the
              card shows both. Rendered only when there IS one: rail and road legs
              have none, and an empty cell on every one of them would be four
              dashes of noise on the commonest case. */}
          {leg.voyageNumber ? (
            <Fact
              label={t(
                leg.mode === 'air' ? 'cases.legs.flightNumber' : 'cases.legs.voyageNumber',
              )}
            >
              <span className="font-mono text-[12.5px]">{leg.voyageNumber}</span>
            </Fact>
          ) : null}

          {/* Transshipment (0062): shown only when the cargo DOES change
              vehicle — "no transshipment" on every leg would be noise. */}
          {leg.transshipment ? (
            <Fact label={t('routePoints.transshipment')}>
              <span>{t('routePoints.transshipmentYes')}</span>
            </Fact>
          ) : null}

          {/* Where this leg was last seen (FR-24), with when it was reported
              under it. `case_legs.last_location` is the only column in the schema
              that answers "where is it now", and it was served on the leg DTO and
              mapped onto the domain type while NO component read it — a fact
              travelling the whole way to the browser to be dropped.

              Rendered only when there is one, like the conveyance above: a
              planned leg has not been anywhere, and an empty cell on every one of
              them would be noise on the commonest card. The case-LEVEL current
              location is a different question — derived from the legs, Story 3.4
              AC 5 — and is deliberately not answered here. */}
          {leg.lastLocation ? (
            <Fact label={t('cases.legs.lastLocation')}>
              <span className="text-text-body">{leg.lastLocation}</span>
              {leg.lastReportedAt ? (
                <span className="text-text-muted mt-0.5 block text-[12px] leading-snug font-light">
                  {t('cases.legs.reportedAt', {
                    at: format.timestamp(leg.lastReportedAt),
                  })}
                </span>
              ) : null}
            </Fact>
          ) : null}

          {/* The direction's money, one fact at a time (issue #35 story 6).
              Composed per leg onto the case detail from finance's own read
              (AD-3, AD-23), so this card and the direction's own page cannot
              disagree about what a haulier is owed.

              Each cell renders on its own presence rather than on one flag,
              because absent here means the composer had no money block at all
              — never "your role may not see it". Figures go to everybody on the
              case and only the counterparty's IDENTITY is suppressed (AD-5
              beside AD-6) — which is why this card can show every figure while
              naming no haulier at all. */}
          {expense ? (
            <Fact
              label={
                leg.actualExpense
                  ? t('cases.legs.expenses')
                  : t('cases.legs.plannedExpense')
              }
            >
              <span className="asa-num">
                {formatMoney(expense.amount, expense.currency)}
              </span>
            </Fact>
          ) : null}

          {leg.paidToCarrier ? (
            <Fact label={t('cases.legs.paidToCarrier')}>
              <span className="asa-num">
                {formatMoney(leg.paidToCarrier.amount, leg.paidToCarrier.currency)}
              </span>
            </Fact>
          ) : null}

          {leg.carrierOutstanding ? (
            <Fact label={t('cases.legs.carrierOutstanding')}>
              {/* The one figure here somebody is phoned about. Amber only when
                  something is actually owed — a settled direction is the quiet,
                  ordinary state and must not wear an attention colour. */}
              <span
                className={cn(
                  'asa-num',
                  Number(leg.carrierOutstanding.amount) > 0 ? 'text-attention' : null,
                )}
              >
                {formatMoney(
                  leg.carrierOutstanding.amount,
                  leg.carrierOutstanding.currency,
                )}
              </span>
            </Fact>
          ) : null}
        </div>

        {/* The boxes (FR-67) as a COUNT, on their own strip. The numbers, the
            seals and the types are the leg page's «Контейнеры» card — itemising
            them here too was the same list twice, and a run of "MSKU … / SL-…"
            pairs wrapped into noise on the commonest card. Rendered only when
            there are any: road legs frequently have none, and an empty strip
            on every truck leg would be a line of nothing. */}
        {boxes > 0 ? (
          <div className="border-line-hair flex items-baseline gap-3 border-t px-[18px] py-2.5">
            <span className="text-text-faint text-[11px] tracking-wide uppercase">
              {t('cases.legs.containers')}
            </span>
            <span className="asa-num text-text-body text-[12.5px]">
              {boxes}
            </span>
          </div>
        ) : null}

        {leg.stalledDays != null ? (
          <div className="border-line-hair bg-attention-soft border-t px-[18px] py-2.5 text-[12.5px] leading-snug">
            <span className="text-attention font-semibold">
              {t('cases.stalled', { count: leg.stalledDays })}.
            </span>
            {leg.stallNote ? (
              <span className="text-text-heading"> {leg.stallNote}</span>
            ) : null}
          </div>
        ) : null}
      </Link>

      {carriers.kind !== 'none' ? (
        <div className="border-line-hair flex flex-wrap items-baseline gap-x-3 gap-y-1 border-t px-[18px] py-2.5">
          <span className="text-text-muted text-[11px] tracking-wide uppercase">
            {t('cases.legs.carrier')}
          </span>
          <LegCarriersLine
            summary={carriers}
            placement="inline"
            className="text-text-body text-[12.5px]"
          />
        </div>
      ) : null}
    </Card>
  )
}
